import React, { useEffect, useState } from 'react';
import { AngularDiProviders } from '../../../../injector/components/angular-di/AngularDiProviders';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../wizard/hooks/useWizardStepData';
import TargetSettingsFormWrapper from '../TargetSettingsFormWrapper/TargetSettingsFormWrapper';
import { NodeConfigData } from '../../../../connector/connector-form/interfaces/nodeFormData';
import { forkJoin, of } from 'rxjs';
import useService from '../../../../injector/hooks/useService';
import { TargetSettingsService } from '../../services/target-settings.service';
import { TARGET_TYPES } from '../../models/target-type';
import { AuthType, ConnectorAuthTypeEnum } from '../../../../connector/connector-form/interfaces/authType';
import { getNextEntityNameForDefaultName } from '../../../../core/utils/entity';
import { HdDocLink } from '../../../../components';
import { ConnectorFormShimmer } from '../../../../connector/connector-form/components/connector-form-shimmer/ConnectorFormShimmer';
import RetryApiAlert from '../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';

const angularDIProviders = [ TargetSettingsService ];

export function TargetSettings({
  initialData,
  controller,
  markDirty,
  onDataChange,
  completeStep,
  setBeforeUnload
}: WizardStepBaseProps) {
  const [ targetData, setTargetData ] = useState(null);
  const [ data, setData ] = useWizardStepData(initialData, onDataChange);

  const onSubmit = (d) => {
    setData((s) => ({
      ...s,
      targetData: d
    }));

    completeStep();
  };

  useEffect(() => {
    setBeforeUnload(() => {
      const newData = { ...data };

      if (!targetData) {
        newData.targetData = {};
      } else {
        newData.targetData = {
          id: targetData.id,
          type: targetData.type,
          ...targetData.formData
        };
      }

      setData(newData);
    });
  }, [ data, targetData ]);

  return (
    <AngularDiProviders providers={angularDIProviders}>
      <TargetSettingsInner
        data={data}
        onTargetDataChange={setTargetData}
        onSubmit={onSubmit}
        markDirty={markDirty}
        updateData={(d) => setData((s) => ({ ...s, ...d }))}
        stepController={controller}
      />
    </AngularDiProviders>
  );
}

export function TargetSettingsInner({
  data,
  onTargetDataChange,
  onSubmit,
  markDirty,
  updateData,
  stepController
}) {
  const maxWidth = '820px';

  const targetType = stepController?.data['targetType'];
  const targetTypeMetaData = TARGET_TYPES[targetType.type];
  const targetDocumentationLink = targetTypeMetaData?.docLink;

  const [ innerData, setInnerData ] = useState({ data, initialDataSet: false });
  const [ targetData, setTargetData ] = useState<NodeConfigData>(null);
  const [ isFormPristineOnInit ] = useState<boolean>(!data?.targetData);

  const [ loadingPage, setLoadingPage ] = useState(true);
  const [ pageLoadError, setPageLoadError ] = useState();

  const targetSettingsService = useService(TargetSettingsService);

  let targetList = [];

  useEffect(() => {
    onTargetDataChange(targetData);
  }, [ targetData ]);

  useEffect(() => {
    appendAuthAccountDetails();
  }, []);

  useEffect(() => {
    if (innerData.initialDataSet) {
      loadTarget();
    }
  }, [ innerData.initialDataSet ]);

  const appendAuthAccountDetails = () => {
    const account = stepController.data['authAccount'];
    const authType: AuthType = stepController.data['authType'];

    if (!account && !authType) {
      setInnerData({
        ...innerData,
        initialDataSet: true
      });

      return;
    }

    const newData = {
      targetData: { ...data.targetData }
    };

    if ('user' in account) {
      newData.targetData.oauthTokenId = account.id;
      newData.targetData.authorisedAccount = account.user;
    } else if ('email' in account) {
      newData.targetData.serviceAccountId = account.id;
      newData.targetData.authorisedAccount = account.email;
    }

    newData.targetData.authType = authType?.value || ConnectorAuthTypeEnum.OAUTH;

    setInnerData({
      data: newData,
      initialDataSet: true
    });

    updateData(newData);
  };

  const loadTarget = () => {
    setLoadingPage(true);
    setPageLoadError(null);

    forkJoin([
      targetSettingsService.getNodeFromExistingData$(
        targetTypeMetaData,
        innerData.data.targetData
      ),
      isFormPristineOnInit ? targetSettingsService.getExistingTargets() : of([]),
    ]).toPromise().then(([target, existingList]) => {
      setLoadingPage(false);
      targetList = existingList;

      if (isFormPristineOnInit) {
        target.formData.name = getDefaultTargetName();
      }

      setTargetData({ ...target });
    }, (err) => {
      setLoadingPage(false);
      setPageLoadError(err);
    });
  };

  const getDefaultTargetName = () => {
    const prefix = `${targetType.typeDisplay} Target`;

    return getNextEntityNameForDefaultName(prefix, targetList, (target) => target.name);
  };

  const dataIdGenerator = getDataIdGenerator('targetSettings');

  return (
    <div className='scrollable-content bg-surface flex-1'>
      <div className='body-container' style={{ maxWidth }}>
        <div className='header-container'>
          <div className='header'>
            Configure your {targetType?.typeDisplay} Target
          </div>

          {!loadingPage && targetDocumentationLink && (
            <div className='subheader'>
              To know more about configuring this Target, read the {' '}
              <HdDocLink
                dataId='target-settings'
                icon='new-window'
                docLink={targetDocumentationLink}
                section='settings_header'
                label='Documentation'
              />
            </div>
          )}
        </div>

        {loadingPage && <ConnectorFormShimmer />}

        {targetType && targetData && !loadingPage && !pageLoadError && (
          <div className='settings-content'>
            <TargetSettingsFormWrapper
              updateSettings={false}
              targetType={targetType}
              targetData={targetData}
              submit={onSubmit}
              emitFormDirty={markDirty}
            />
          </div>
        )}

        {!loadingPage && pageLoadError && (
          <RetryApiAlert
            error={pageLoadError}
            actionHandler={loadTarget}
            dataId={dataIdGenerator('')}
          />
        )}
      </div>
    </div>
  );
}
